import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Printer, Truck, AlertTriangle, CheckCircle, BookOpen, Calendar } from 'lucide-react';
import SiteHeader from '../components/SiteHeader';
import { PRINT_POLICY_VERSION } from '../content/printPolicy';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const opts = { withCredentials: true };

const CA_PROVINCES = [['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'],
  ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'], ['NT', 'Northwest Territories'], ['NU', 'Nunavut'],
  ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'], ['YT', 'Yukon']];
const US_STATES = ('AL AK AZ AR CA CO CT DE DC FL GA HI ID IL IN IA KS KY LA ME MD MA MI MN MS MO MT NE NV NH NJ NM '
  + 'NY NC ND OH OK OR PA RI SC SD TN TX UT VT VA WA WV WI WY PR').split(' ');
const COUNTRIES = ['CA', 'US', 'GB', 'IE', 'PT', 'ES', 'FR', 'DE', 'IT', 'NL', 'BE', 'CH', 'AT', 'DK', 'SE', 'NO',
  'FI', 'PL', 'CZ', 'AU', 'NZ', 'JP', 'SG', 'BR', 'MX'];
const STATUS_LABELS = {
  draft: 'Not ordered yet', awaiting_payment: 'Waiting for payment', paid: 'Paid', submitted: 'Sent to the printer',
  in_production: 'Being printed', shipped: 'Shipped', delivered: 'Delivered', failed: 'Problem — we are on it',
  canceled: 'Canceled', refunded: 'Refunded',
};
const FIELD_LABELS = {
  first_name: 'First name', last_name: 'Last name', company: 'Company (optional)', address_line1: 'Street address',
  address_line2: 'Apartment, suite (optional)', city: 'City', state: 'Province / state', post_code: 'Postal / ZIP code',
  country: 'Country', email: 'Email', phone: 'Phone (optional)',
};

function countryName(code) {
  try { return new Intl.DisplayNames([navigator.language || 'en'], { type: 'region' }).of(code); } catch { return code; }
}

function money(cents, currency) {
  if (cents == null) return '';
  return new Intl.NumberFormat(currency === 'cad' ? 'en-CA' : 'en-US', { style: 'currency', currency: currency.toUpperCase(), currencyDisplay: 'code' }).format(cents / 100);
}

const EMPTY_ADDRESS = { first_name: '', last_name: '', company: '', address_line1: '', address_line2: '', city: '',
  state: '', post_code: '', country: 'CA', email: '', phone: '' };

export default function PrintOrderPage() {
  const { auraId } = useParams();
  const [params] = useSearchParams();
  const [country, setCountry] = useState('CA');
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [sku, setSku] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [order, setOrder] = useState(null);
  const [address, setAddress] = useState(EMPTY_ADDRESS);
  const [fieldErrors, setFieldErrors] = useState({});
  const [method, setMethod] = useState('');
  const [acceptLowRes, setAcceptLowRes] = useState(false);
  const [acceptPolicy, setAcceptPolicy] = useState(false);
  const [busy, setBusy] = useState('');
  const poll = useRef(null);

  const load = useCallback(async (c) => {
    try {
      const { data: d } = await axios.get(`${API_URL}/api/auras/${auraId}/print`, { ...opts, params: { country: c } });
      setData(d);
    } catch (e) { setError(e.response?.data?.detail || 'Could not load printed products.'); }
  }, [auraId]);

  useEffect(() => { load(country); }, [load, country]);

  const loadOrder = useCallback(async (id) => {
    try {
      const { data: o } = await axios.get(`${API_URL}/api/print/orders/${id}`, opts);
      setOrder(o);
      if (o.address) setAddress({ ...EMPTY_ADDRESS, ...o.address });
      return o;
    } catch { return null; }
  }, []);

  useEffect(() => {
    const id = params.get('order');
    if (id) loadOrder(id);
    return () => clearInterval(poll.current);
  }, [params, loadOrder]);

  // While the print file renders, poll the order.
  useEffect(() => {
    clearInterval(poll.current);
    if (order && ['pending', 'rendering'].includes(order.files.status) && order.address) {
      poll.current = setInterval(async () => {
        const o = await loadOrder(order.id);
        if (!o || !['pending', 'rendering'].includes(o.files.status)) clearInterval(poll.current);
      }, 3000);
    }
    return () => clearInterval(poll.current);
  }, [order?.id, order?.files?.status]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    setBusy('start'); setError('');
    try {
      const { data: o } = await axios.post(`${API_URL}/api/auras/${auraId}/print/orders`, { sku, quantity }, opts);
      setOrder(o); setMethod('');
    } catch (e) { setError(e.response?.data?.detail || 'Could not start the order.'); }
    finally { setBusy(''); }
  };

  const quote = async (ev) => {
    ev.preventDefault();
    setBusy('quote'); setError(''); setFieldErrors({});
    try {
      const { data: o } = await axios.post(`${API_URL}/api/print/orders/${order.id}/quote`, { address }, opts);
      setOrder(o); setMethod(o.shipping_options?.[0]?.uid || '');
    } catch (e) {
      const d = e.response?.data?.detail;
      if (d && d.fields) { setFieldErrors(d.fields); setError(d.message); } else setError(d || 'Could not get a shipping quote.');
    } finally { setBusy(''); }
  };

  const pay = async () => {
    setBusy('pay'); setError('');
    try {
      const { data: r } = await axios.post(`${API_URL}/api/print/orders/${order.id}/checkout`,
        { shipment_method_uid: method, accept_low_resolution: acceptLowRes, accept_policy: acceptPolicy, policy_version: PRINT_POLICY_VERSION }, opts);
      window.location.href = r.url;
    } catch (e) { setError(e.response?.data?.detail || 'Could not start the payment.'); setBusy(''); }
  };

  const cancel = async (id) => {
    try {
      await axios.post(`${API_URL}/api/print/orders/${id}/cancel`, {}, opts);
      if (order && order.id === id) setOrder(null);
      load(country);
    } catch (e) { setError(e.response?.data?.detail || 'Could not cancel.'); }
  };

  if (!data) {
    return <div className="min-h-screen bg-midnight flex items-center justify-center"><div className="text-champagne animate-pulse font-serif text-xl">{error || 'Loading…'}</div></div>;
  }

  const currency = order ? order.currency : data.currency;
  const product = data.products.find((p) => p.sku === sku);
  const options = order?.shipping_options || [];
  const chosen = options.find((o) => o.uid === method);
  const filesReady = order?.files?.status === 'ready';
  const lowRes = order?.low_res || [];
  const editable = order && ['draft', 'awaiting_payment'].includes(order.status);
  const justPaid = params.get('paid') === '1';
  const setField = (k, v) => setAddress((a) => ({ ...a, [k]: v }));

  const field = (k, props = {}) => (
    <label className="block" key={k}>
      <span className="text-sm text-text-muted block mb-1">{FIELD_LABELS[k]}</span>
      <input className="aura-input w-full" value={address[k]} onChange={(e) => setField(k, e.target.value)}
        aria-invalid={!!fieldErrors[k]} aria-describedby={fieldErrors[k] ? `err-${k}` : undefined} {...props} />
      {fieldErrors[k] && <span id={`err-${k}`} className="text-xs text-red-300">{fieldErrors[k]}</span>}
    </label>
  );

  return (
    <div className="min-h-screen bg-midnight">
      <SiteHeader />
      <div className="border-b border-champagne/10 px-4 py-3">
        <div className="max-w-4xl mx-auto">
          <Link to={`/aura/${auraId}`} className="text-text-muted hover:text-champagne flex items-center gap-2 text-sm">
            <ArrowLeft className="w-4 h-4" /> Back to {data.aura.title}
          </Link>
        </div>
      </div>
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        <div>
          <h1 className="text-2xl sm:text-3xl font-serif text-text-light flex items-center gap-2"><Printer className="w-6 h-6 text-champagne" /> Order prints</h1>
          <p className="text-text-muted text-sm mt-1">A printed photo book or calendar of {data.aura.title}, made and shipped by our print partner. Prices are in {currency === 'cad' ? 'Canadian dollars for Canadian addresses' : 'US dollars'}; shipping is quoted for your address.</p>
        </div>

        {error && <div role="alert" className="rounded-lg border border-red-500/40 bg-red-500/10 text-red-300 text-sm px-4 py-3">{error}</div>}
        {justPaid && order && (
          <div role="status" className="aura-card rounded-2xl p-4 border border-champagne/40 text-text-light flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-champagne" /> Thank you! Order {order.ref} is {STATUS_LABELS[order.status]?.toLowerCase()}. We&rsquo;ll email you when it ships.
          </div>
        )}

        {!data.enabled && (
          <div className="aura-card rounded-2xl p-6 text-text-muted" data-testid="print-disabled">Printed books and calendars are coming soon.</div>
        )}

        {data.enabled && !order && (
          <section className="aura-card rounded-2xl p-6 space-y-4" aria-labelledby="pick">
            <h2 id="pick" className="text-lg font-serif text-text-light">1. Choose a product</h2>
            <label className="block max-w-xs">
              <span className="text-sm text-text-muted block mb-1">Shipping to</span>
              <select className="aura-input w-full" value={country} onChange={(e) => { setCountry(e.target.value); setAddress((a) => ({ ...a, country: e.target.value, state: '' })); }}>
                {COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
              </select>
            </label>
            <fieldset>
              <legend className="sr-only">Product</legend>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.products.map((p) => (
                  <label key={p.sku} className={`rounded-xl border p-4 cursor-pointer ${sku === p.sku ? 'border-champagne' : 'border-white/10'} ${p.unavailable ? 'opacity-50' : ''}`}>
                    <input type="radio" name="sku" className="sr-only" value={p.sku} checked={sku === p.sku} disabled={!!p.unavailable} onChange={() => setSku(p.sku)} />
                    <div className="flex items-center gap-2 text-text-light">
                      {p.kind === 'photobook' ? <BookOpen className="w-4 h-4 text-champagne" /> : <Calendar className="w-4 h-4 text-champagne" />}
                      <span className="font-medium">{p.name}</span>
                    </div>
                    <p className="text-xs text-text-muted mt-1">{p.description}</p>
                    <p className="text-sm text-champagne mt-2">{money(p.price_cents, data.currency)}{p.page_count ? ` · ${p.page_count} pages` : ''}</p>
                    {p.unavailable && <p className="text-xs text-red-300 mt-1">{p.unavailable}</p>}
                  </label>
                ))}
              </div>
            </fieldset>
            {product?.kind === 'photobook' && (
              <p className="text-xs text-text-muted">Your book uses your saved photo book design ({data.book_photo_count} photos). <Link className="underline" to={`/aura/${auraId}/photo-book`}>Photo book settings</Link></p>
            )}
            {product && product.kind !== 'photobook' && (
              <p className="text-xs text-text-muted">Your calendar uses your saved calendar design. <Link className="underline" to={`/aura/${auraId}/calendar`}>Design the calendar</Link></p>
            )}
            <div className="flex items-end gap-3">
              <label className="block w-24">
                <span className="text-sm text-text-muted block mb-1">Quantity</span>
                <input type="number" min={1} max={data.max_quantity} className="aura-input w-full" value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Math.min(data.max_quantity, Number(e.target.value) || 1)))} />
              </label>
              <button className="btn-champagne px-4 py-2 text-sm disabled:opacity-50" disabled={!sku || busy === 'start'} onClick={start} data-testid="print-start">
                {busy === 'start' ? 'Starting…' : 'Continue'}
              </button>
            </div>
          </section>
        )}

        {order && editable && (
          <form className="aura-card rounded-2xl p-6 space-y-4" onSubmit={quote} aria-labelledby="addr" noValidate>
            <h2 id="addr" className="text-lg font-serif text-text-light">2. Shipping address</h2>
            <p className="text-sm text-text-muted">{order.quantity} × {order.product.name}{order.page_count ? ` (${order.page_count} pages)` : ''}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {field('first_name', { autoComplete: 'given-name', required: true })}
              {field('last_name', { autoComplete: 'family-name', required: true })}
              {field('address_line1', { autoComplete: 'address-line1', required: true })}
              {field('address_line2', { autoComplete: 'address-line2' })}
              {field('city', { autoComplete: 'address-level2', required: true })}
              <label className="block">
                <span className="text-sm text-text-muted block mb-1">{FIELD_LABELS.country}</span>
                <select className="aura-input w-full" value={address.country} autoComplete="country"
                  onChange={(e) => { setField('country', e.target.value); setField('state', ''); }}>
                  {COUNTRIES.map((c) => <option key={c} value={c}>{countryName(c)}</option>)}
                </select>
              </label>
              {['CA', 'US'].includes(address.country) ? (
                <label className="block">
                  <span className="text-sm text-text-muted block mb-1">{address.country === 'CA' ? 'Province / territory' : 'State'}</span>
                  <select className="aura-input w-full" value={address.state} autoComplete="address-level1"
                    aria-invalid={!!fieldErrors.state} aria-describedby={fieldErrors.state ? 'err-state' : undefined}
                    onChange={(e) => setField('state', e.target.value)}>
                    <option value="">Choose…</option>
                    {address.country === 'CA' ? CA_PROVINCES.map(([c, n]) => <option key={c} value={c}>{n}</option>)
                      : US_STATES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                  {fieldErrors.state && <span id="err-state" className="text-xs text-red-300">{fieldErrors.state}</span>}
                </label>
              ) : field('state', { autoComplete: 'address-level1' })}
              {field('post_code', { autoComplete: 'postal-code' })}
              {field('email', { type: 'email', autoComplete: 'email', required: true })}
              {field('phone', { type: 'tel', autoComplete: 'tel' })}
            </div>
            <button type="submit" className="btn-outline px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-50" disabled={busy === 'quote'} data-testid="print-quote">
              <Truck className="w-4 h-4" /> {busy === 'quote' ? 'Getting shipping prices…' : 'Get shipping options'}
            </button>
          </form>
        )}

        {order && editable && options.length > 0 && (
          <section className="aura-card rounded-2xl p-6 space-y-4" aria-labelledby="ship">
            <h2 id="ship" className="text-lg font-serif text-text-light">3. Shipping and payment</h2>
            <fieldset>
              <legend className="sr-only">Shipping method</legend>
              <div className="space-y-2">
                {options.map((o) => (
                  <label key={o.uid} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-4 py-3 text-sm text-text-light">
                    <span className="flex items-center gap-2">
                      <input type="radio" name="method" value={o.uid} checked={method === o.uid} onChange={() => setMethod(o.uid)} />
                      {o.name}{o.min_days != null ? ` · ${o.min_days}–${o.max_days} business days` : ''}
                    </span>
                    <span>{money(o.cents, order.currency)}</span>
                  </label>
                ))}
              </div>
            </fieldset>
            <div aria-live="polite" className="text-sm text-text-muted">
              {!filesReady && order.files.status !== 'failed' && 'Preparing your print file… this can take a minute.'}
              {order.files.status === 'failed' && <span className="text-red-300">{order.files.error}</span>}
              {filesReady && `Print file ready (${order.files.pages} pages).`}
            </div>
            {filesReady && lowRes.length > 0 && (
              <div className="rounded-lg border border-amber-400/40 bg-amber-400/10 p-4 text-sm text-amber-200" role="alert">
                <p className="flex items-center gap-2 font-medium"><AlertTriangle className="w-4 h-4" /> {lowRes.length} photo{lowRes.length > 1 ? 's' : ''} may print blurry</p>
                <ul className="list-disc ml-6 mt-1 text-xs">
                  {lowRes.slice(0, 10).map((x) => <li key={x.media_id}>{x.where}: about {x.dpi} DPI (150+ looks sharp)</li>)}
                </ul>
                <label className="flex items-center gap-2 mt-2"><input type="checkbox" checked={acceptLowRes} onChange={(e) => setAcceptLowRes(e.target.checked)} /> I understand and want to print them anyway</label>
              </div>
            )}
            <dl className="text-sm text-text-light grid grid-cols-2 gap-y-1 max-w-sm">
              <dt className="text-text-muted">{order.quantity} × {order.product.name}</dt><dd className="text-right">{money(order.item_price_cents * order.quantity, order.currency)}</dd>
              <dt className="text-text-muted">Shipping</dt><dd className="text-right">{chosen ? money(chosen.cents, order.currency) : '—'}</dd>
              <dt className="font-medium">Total before tax</dt><dd className="text-right font-medium">{chosen ? money(order.item_price_cents * order.quantity + chosen.cents, order.currency) : '—'}</dd>
            </dl>
            <label className="flex items-start gap-2 text-sm text-text-light max-w-xl">
              <input type="checkbox" className="mt-1" checked={acceptPolicy} onChange={(e) => setAcceptPolicy(e.target.checked)} data-testid="print-accept-policy" />
              <span>
                I've checked the preview and agree to the{' '}
                <Link to="/policies/print-orders" target="_blank" rel="noopener" className="underline text-champagne">print order policy</Link>:
                printed items are made to order by a third-party print partner and <strong>all sales are final</strong>,
                except for damaged, defective or misprinted items.
              </span>
            </label>
            <div className="flex gap-3 flex-wrap">
              <button className="btn-champagne px-5 py-2 text-sm disabled:opacity-50" onClick={pay} data-testid="print-pay"
                disabled={!chosen || !filesReady || (lowRes.length > 0 && !acceptLowRes) || !acceptPolicy || busy === 'pay'}>
                {busy === 'pay' ? 'Opening payment…' : 'Pay securely'}
              </button>
              <button type="button" className="btn-outline px-4 py-2 text-sm" onClick={() => cancel(order.id)}>Cancel order</button>
            </div>
          </section>
        )}

        {data.orders.length > 0 && (
          <section className="aura-card rounded-2xl p-6" aria-labelledby="hist">
            <h2 id="hist" className="text-lg font-serif text-text-light mb-3">Your orders</h2>
            <ul className="divide-y divide-white/10" data-testid="print-orders">
              {data.orders.map((o) => (
                <li key={o.id} className="py-3 flex flex-wrap items-center justify-between gap-2 text-sm">
                  <div className="text-text-light">
                    <span className="font-medium">{o.product.name}</span> × {o.quantity}
                    <span className="text-text-muted"> · {o.ref} · {new Date(o.created_at).toLocaleDateString()}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-champagne">{STATUS_LABELS[o.status] || o.status}</span>
                    {o.total_cents > 0 && <span className="text-text-muted">{money(o.total_cents, o.currency)}</span>}
                    {o.tracking_url && <a className="underline text-text-light" href={o.tracking_url} target="_blank" rel="noopener noreferrer">Track</a>}
                    {['draft', 'awaiting_payment'].includes(o.status) && (
                      <>
                        <button type="button" className="underline text-text-light" onClick={() => loadOrder(o.id)}>Continue</button>
                        <button type="button" className="underline text-text-muted" onClick={() => cancel(o.id)}>Cancel</button>
                      </>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
